import { format, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import type { Factura } from "../types/index";

interface Nota {
  tipo: 'credito' | 'debito';
  numero: string;
  fecha: string;
  motivo: string;
  monto: number;
}

export const generateFacturaPDF = async (
  factura: Factura,
  notas: Nota[] = [],
  onSuccess: () => void,
  onError: (error: any) => void
): Promise<void> => {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Document dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    
    // Enhanced color palette with additional shades for depth
    const colors = {
      background: "#FFFFFF",
      primary: "#000000",
      text: "#1F2937",        // Slightly softer black for better readability
      textLight: "#6B7280",   // Gray-500
      border: "#E5E7EB",      // Gray-200 
      blue: "#2563EB",        // Blue-600
      blueLight: "#DBEAFE",   // Blue-100
      blueAccent: "#1E40AF",  // Darker blue for important elements
      success: "#059669",     // Deeper green for better visibility
      successLight: "#D1FAE5", // Green-100
      warning: "#D97706",     // Deeper amber for better visibility
      warningLight: "#FEF3C7", // Amber-100
      danger: "#DC2626",      // Deeper red for better visibility
      dangerLight: "#FEE2E2", // Red-100
      grayBg: "#F9FAFB",      // Gray-50
      grayMid: "#E5E7EB",     // Gray-200 for borders and dividers
    };

    // Function to add footer to the current page
    const addFooter = (pageNum: number) => {
      // Moved footer closer to the bottom of the page
      const footerY = pageHeight - 15;
      
      // Footer border with lighter styling
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);
      
      // Reduced font size for all footer text
      doc.setFontSize(7);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "normal");
      
      // Simplified footer text - combined the legal text into a single line
      doc.text("Factura generada automáticamente. Por favor, conserve este documento.", margin, footerY - 5);
      
      // Page number
      doc.text(`Página ${pageNum}`, pageWidth - margin, footerY - 5, { align: "right" });
      
      // Simplified contact info - combining elements to save space
      doc.setFont("helvetica", "bold");
      doc.text("Destiny Caribbean Tours", margin, footerY);
      
      // Combined contact info on a single line in the center with separators
      const contactInfo = 'contacto@destinycaribbeantours.com | +1 809 285-3179 | www.destinycaribbeantours.com';
      doc.setFont("helvetica", "normal");
      doc.text(contactInfo, pageWidth / 2, footerY, { align: "center" });
      
      // RNC (tax ID) on the right
      doc.setFont("helvetica", "bold");
      doc.text('RNC: 131361765', pageWidth - margin, footerY, { align: "right" });
    };
    
    // Function to check if we need a new page and handle page break
    const checkPageBreak = (currentY: number, neededHeight: number): number => {
      // Reduced required footer space from 45mm to 30mm due to more compact footer
      if (currentY + neededHeight > pageHeight - 30) {
        // Add footer to current page
        addFooter(doc.getNumberOfPages());
        
        // Add a new page
        doc.addPage();
        
        // Return the new Y position at the top of the new page with some margin
        return margin + 15;
      }
      
      // No page break needed, continue with current position
      return currentY;
    };
    
    // -------- HEADER SECTION --------
    let currentY = 25;
    
    // Invoice label with better spacing
    doc.setFontSize(22);
    doc.setTextColor(colors.text);
    doc.setFont("helvetica", "bold");
    doc.text(`FACTURA`, margin, currentY + 5);
    
    // Invoice number with accent color
    doc.setFontSize(16);
    doc.setTextColor(colors.blueAccent);
    doc.setFont("helvetica", "bold");
    doc.text(`#${factura.numero}`, margin, currentY + 15);
    
    // Right section - date and status with improved alignment
    doc.setFontSize(9);
    doc.setTextColor(colors.textLight);
    doc.setFont("helvetica", "normal");
    doc.text("Fecha:", pageWidth - margin - 37, currentY + 1);
    
    // Date value with better hierarchy
    doc.setFontSize(10);
    doc.setTextColor(colors.text);
    doc.setFont("helvetica", "bold");
    const fechaStr = format(parseISO(factura.fecha), 'dd/MM/yyyy');
    doc.text(fechaStr, pageWidth - margin, currentY + 1, { align: "right" });
    
    // Status label
    doc.setFontSize(9);
    doc.setTextColor(colors.textLight);
    doc.setFont("helvetica", "normal");
    doc.text("Estado:", pageWidth - margin - 37, currentY + 8);
    
    // Status value with improved visual treatment
    let statusText = '';
    let statusTextColor = '';
    
    if (factura.estado === 'pagada') {
      statusText = 'PAGADA';
      statusTextColor = colors.success;
    } else if (factura.estado === 'pendiente') {
      statusText = 'PENDIENTE';
      statusTextColor = colors.warning;
    } else {
      statusText = 'ANULADA';
      statusTextColor = colors.danger;
    }
    
    // Status text with small caps for better professional look
    doc.setFontSize(10);
    doc.setTextColor(statusTextColor);
    doc.setFont("helvetica", "bold");
    doc.text(statusText, pageWidth - margin, currentY + 8, { align: "right" });
    
    currentY += 25; // Increased spacing after header
    
    // Border line below header - slightly thicker for better visual separation
    doc.setDrawColor(colors.grayMid);
    doc.setLineWidth(0.7);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    
    currentY += 20; // Increased spacing after border
    
    // -------- CLIENT & BILLING INFO SECTION --------
    
    // Left column - Client info with accent left border
    doc.setDrawColor(colors.blueAccent);
    doc.setLineWidth(1.7);
    doc.line(margin, currentY, margin, currentY + 30); // Increased height for better spacing
    
    doc.setFontSize(9);
    doc.setTextColor(colors.textLight);
    doc.setFont("helvetica", "bold");
    doc.text("CLIENTE", margin + 5, currentY);
    
    currentY += 8; // Increased spacing after label
    
    // Client name with better typography
    doc.setFontSize(12);
    doc.setTextColor(colors.text);
    doc.setFont("helvetica", "bold");
    doc.text(factura.clienteNombre || 'Sin nombre', margin + 5, currentY);
    
    currentY += 10; // Increased spacing after name
    
    // Client type with refined styling
    let clientTypeText;
    let clientTypeColor;
    
    if (factura.manualInvoice) {
      clientTypeText = "Manual";
      clientTypeColor = colors.blueAccent;
    } else if (factura.clientType === 'empresa') {
      clientTypeText = "Empresa (B2B)";
      clientTypeColor = "#7C3AED"; // Deeper purple for better visibility
    } else {
      clientTypeText = "Individual (B2C)";
      clientTypeColor = colors.blueAccent;
    }
    
    // Client type text with better visual distinction
    doc.setFontSize(9);
    doc.setTextColor(clientTypeColor);
    doc.setFont("helvetica", "bold");
    doc.text(clientTypeText, margin + 5, currentY);
    
    // Right column - Invoice details with better design
    const detailsX = margin + contentWidth/2;
    currentY = currentY - 18; // Reset to start of section
    
    doc.setDrawColor("#9CA3AF"); // Gray-400 for better visibility
    doc.setLineWidth(1.7);
    doc.line(detailsX, currentY, detailsX, currentY + 30); // Increased height for better spacing
    
    doc.setFontSize(9);
    doc.setTextColor(colors.textLight);
    doc.setFont("helvetica", "bold");
    doc.text("DETALLES DE FACTURACIÓN", detailsX + 5, currentY);
    
    currentY += 8; // Increased spacing after label
    
    // Add invoice details with improved alignment and spacing
    const addDetailRow = (label: string, value: string, y: number) => {
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "normal");
      doc.text(label, detailsX + 5, y);
      
      doc.setTextColor(colors.text);
      doc.setFont("helvetica", "bold");
      doc.text(value, detailsX + 38, y);
      
      return y + 8; // Increased spacing between rows
    };
    
    currentY = addDetailRow("Número de factura:", factura.numero || '-', currentY);
    currentY = addDetailRow("Fecha de emisión:", fechaStr, currentY);
    
    // Add service date if available
    if (factura.fechaServicio) {
      const fechaServicioStr = format(parseISO(factura.fechaServicio), 'dd/MM/yyyy');
      currentY = addDetailRow("Fecha de servicio:", fechaServicioStr, currentY);
    } else if (factura.lineas?.[0]?.fechaServicio) {
      const fechaServicioStr = format(parseISO(factura.lineas[0].fechaServicio), 'dd/MM/yyyy');
      currentY = addDetailRow("Fecha de servicio:", fechaServicioStr, currentY);
    }
    
    currentY += 25; // Increased space after client info for better section separation
    
    // -------- ITEMS TABLE SECTION --------
    
    // Table header with title and improved typography
    doc.setFontSize(10);
    doc.setTextColor(colors.blueAccent);
    doc.setFont("helvetica", "bold");
    doc.text("ELEMENTOS FACTURADOS", margin, currentY);
    
    doc.setDrawColor(colors.grayMid);
    doc.setLineWidth(0.7);
    doc.line(margin, currentY + 5, pageWidth - margin, currentY + 5);
    
    currentY += 15; // Increased spacing after header
    
    // Table header with improved spacing
    const cols = [
      { header: 'Descripción', x: margin, width: contentWidth * 0.4, align: 'left' },
      { header: 'Cantidad', x: margin + contentWidth * 0.4, width: contentWidth * 0.15, align: 'center' },
      { header: 'Precio', x: margin + contentWidth * 0.55, width: contentWidth * 0.15, align: 'right' },
      { header: 'Impuesto', x: margin + contentWidth * 0.7, width: contentWidth * 0.15, align: 'right' },
      { header: 'Total', x: margin + contentWidth * 0.85, width: contentWidth * 0.15, align: 'right' }
    ];
    
    // Draw table headers with improved visual hierarchy
    doc.setFontSize(9);
    doc.setTextColor(colors.textLight);
    
    cols.forEach(col => {
      doc.setFont("helvetica", "bold");
      if (col.align === 'left') {
        doc.text(col.header.toUpperCase(), col.x, currentY);
      } else if (col.align === 'center') {
        doc.text(col.header.toUpperCase(), col.x + col.width/2, currentY, { align: "center" });
      } else {
        doc.text(col.header.toUpperCase(), col.x + col.width, currentY, { align: "right" });
      }
    });
    
    currentY += 8; // Increased spacing after headers
    
    // Draw line below headers - slightly thicker for better visual separation
    doc.setDrawColor(colors.grayMid);
    doc.setLineWidth(0.3);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    
    currentY += 8; // Increased spacing after line
    
    // Draw table rows with improved visual design
    doc.setTextColor(colors.text);
    doc.setFontSize(9.5); // Slightly smaller for better fit
    
    factura.lineas.forEach((linea, index) => {
      // Check if we need a new page for this row
      const rowHeight = 10; // Approximate height for each row
      currentY = checkPageBreak(currentY, rowHeight);
      
      // Alternate row background with slightly more pronounced effect
      if (index % 2 === 0) {
        doc.setFillColor(colors.grayBg);
        doc.rect(margin, currentY - 4, contentWidth, 8, 'F');
      }
      
      // Draw cell values with better readability
      doc.setFont("helvetica", "normal");
      doc.text(linea.descripcion.substring(0, 35) + (linea.descripcion.length > 35 ? '...' : ''), cols[0].x, currentY);
      
      doc.text(linea.cantidad.toString(), cols[1].x + cols[1].width/2, currentY, { align: "center" });
      
      // Currency formatted with better spacing
      doc.text('$' + (linea.precioUnitario !== undefined && linea.precioUnitario !== null ? Number(linea.precioUnitario).toFixed(2) : '0.00'), cols[2].x + cols[2].width, currentY, { align: "right" });
      
      doc.text('$' + (linea.impuesto !== undefined && linea.impuesto !== null ? linea.impuesto.toFixed(2) : '0.00'), cols[3].x + cols[3].width, currentY, { align: "right" });
      
      // Total with better visual hierarchy
      doc.setFont("helvetica", "bold");
      doc.text('$' + (linea.total !== undefined && linea.total !== null ? linea.total.toFixed(2) : '0.00'), cols[4].x + cols[4].width, currentY, { align: "right" });
      
      currentY += 10; // Increased spacing between rows
    });
    
    currentY += 10; // Increased spacing after table
    
    // After drawing the items table, before adding notas section
    currentY += 5;
    
    // Check if we need a page break before adding notas section
    if (notas && notas.length > 0) {
      // Calculate approximately how much space we need for the notas section
      const notasHeight = 25 + (notas.length * 12); // Header + rows with increased spacing
      
      // Check and handle page break if needed
      currentY = checkPageBreak(currentY, notasHeight);
      
      // Add a title for notas section
      currentY += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(colors.blueAccent);
      doc.setFont("helvetica", "bold");
      doc.text("NOTAS DE CRÉDITO Y DÉBITO", margin, currentY);
      
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.3);
      doc.line(margin, currentY + 5, pageWidth - margin, currentY + 5);
      
      currentY += 15; // Increased spacing after header
      
      // Table header for notas
      const notasCols = [
        { header: 'Tipo', x: margin, width: contentWidth * 0.2, align: 'left' },
        { header: 'Número', x: margin + contentWidth * 0.2, width: contentWidth * 0.2, align: 'left' },
        { header: 'Fecha', x: margin + contentWidth * 0.4, width: contentWidth * 0.2, align: 'left' },
        { header: 'Motivo', x: margin + contentWidth * 0.6, width: contentWidth * 0.2, align: 'left' },
        { header: 'Monto', x: margin + contentWidth * 0.8, width: contentWidth * 0.2, align: 'right' }
      ];
      
      // Draw nota table headers
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      
      notasCols.forEach(col => {
        doc.setFont("helvetica", "bold");
        if (col.align === 'left') {
          doc.text(col.header.toUpperCase(), col.x, currentY);
        } else {
          doc.text(col.header.toUpperCase(), col.x + col.width, currentY, { align: "right" });
        }
      });
      
      currentY += 8; // Increased spacing after headers
      
      // Draw line below nota headers
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.3);
      doc.line(margin, currentY, pageWidth - margin, currentY);
      
      currentY += 8; // Increased spacing after line
      
      // Draw table rows for notas
      doc.setTextColor(colors.text);
      doc.setFontSize(9);
      
      notas.forEach((nota, index) => {
        // Check if we need a new page for this row
        const rowHeight = 10; // Approximate height for each row
        currentY = checkPageBreak(currentY, rowHeight);
        
        // Alternate row background
        if (index % 2 === 0) {
          doc.setFillColor(colors.grayBg);
          doc.rect(margin, currentY - 4, contentWidth, 8, 'F');
        }
        
        // Draw cell values
        doc.setFont("helvetica", "normal");
        
        // Tipo with color-coding
        const tipoText = nota.tipo === 'credito' ? 'Crédito' : 'Débito';
        doc.setTextColor(nota.tipo === 'credito' ? colors.success : colors.danger);
        doc.setFont("helvetica", "bold");
        doc.text(tipoText, notasCols[0].x, currentY);
        
        // Reset color for other fields
        doc.setTextColor(colors.text);
        doc.setFont("helvetica", "normal");
        
        doc.text(nota.numero, notasCols[1].x, currentY);
        
        const fechaStr = format(new Date(nota.fecha), 'dd/MM/yyyy');
        doc.text(fechaStr, notasCols[2].x, currentY);
        
        doc.text(nota.motivo.charAt(0).toUpperCase() + nota.motivo.slice(1), notasCols[3].x, currentY);
        
        // Monto with sign (negative for credit notes, positive for debit notes)
        doc.setTextColor(nota.tipo === 'credito' ? colors.danger : colors.success);
        doc.setFont("helvetica", "bold");
        const sign = nota.tipo === 'credito' ? '-' : '+';
        doc.text(`${sign}$${nota.monto.toFixed(2)}`, notasCols[4].x + notasCols[4].width, currentY, { align: "right" });
        
        currentY += 10; // Increased spacing between rows
      });
      
      // Reset text color
      doc.setTextColor(colors.text);
      doc.setFont("helvetica", "normal");
      
      // Add space after notas section
      currentY += 10;
    }
    
    // Check for page break before adding summary section
    // Use a more reasonable height that won't force a page break unnecessarily
    const summaryHeight = 60; 
    currentY = checkPageBreak(currentY, summaryHeight);
    
    // -------- SUMMARY SECTION --------
    // Right-aligned financial summary with improved styling
    const summaryWidth = 100; // Increased width for better alignment
    const summaryX = pageWidth - margin - summaryWidth;
    
    // Subtle line above summary
    doc.setDrawColor(colors.grayMid);
    doc.setLineWidth(0.1);
    doc.line(summaryX, currentY, pageWidth - margin, currentY);
    
    currentY += 8; // Reduced spacing after line
    
    // Add summary rows with elegant, minimalist styling
    const addSummaryRow = (label: string, value: string, y: number, isBold: boolean = false, isTotal: boolean = false) => {
      // Label styling - consistent gray for all labels
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "normal");
      doc.text(label, summaryX, y);
      
      // Value styling - consistent dark text with bold for important items
      doc.setFontSize(isBold ? 10 : 9);
      doc.setTextColor(colors.text);
      doc.setFont("helvetica", isBold ? "bold" : "normal");
      
      // Total gets slightly larger font
      if (isTotal) {
        doc.setFontSize(11);
      }
      
      doc.text(value, pageWidth - margin, y, { align: "right" });
      
      return y + 7; // Consistent tight spacing between rows
    };
    
    // Draw summary section with clean, minimalist hierarchy
    let summaryY = currentY + 3;

    // First show original subtotal if there's a discount
    if (factura.reservation && factura.reservation.descuento > 0) {
      const descuento = factura.reservation.descuento;
      const originalSubtotal = factura.subtotal / (1 - (descuento / 100));
      const discountAmount = originalSubtotal * (descuento / 100);
      
      // Original subtotal before discount
      summaryY = addSummaryRow("Subtotal (antes de descuento):", `$${originalSubtotal.toFixed(2)}`, summaryY);
      
      // Show discount with parentheses for negative
      summaryY = addSummaryRow("Descuento aplicado:", `${descuento}% ($${discountAmount.toFixed(2)})`, summaryY);
      
      // Then show subtotal after discount
      summaryY = addSummaryRow("Subtotal:", `$${factura.subtotal.toFixed(2)}`, summaryY);
    } else {
      // If no discount, just show the regular subtotal
      summaryY = addSummaryRow("Subtotal:", `$${factura.subtotal.toFixed(2)}`, summaryY);
    }
    
    // Show taxes
    summaryY = addSummaryRow("Impuestos:", `$${factura.impuestoTotal.toFixed(2)}`, summaryY);
    
    // Add an elegant separator line before total
    doc.setDrawColor(colors.grayMid);
    doc.setLineWidth(0.2);
    const lineY = summaryY - 3;
    doc.line(summaryX + 30, lineY, pageWidth - margin, lineY);
    
    // Add more space before total
    summaryY += 2;
    
    // Show total amount with slight emphasis
    summaryY = addSummaryRow("Total:", `$${factura.total.toFixed(2)}`, summaryY, true, true);
    
    // Add payment information if the invoice is pending
    if (factura.estado === 'pendiente') {
      // Add more space for payment section
      summaryY += 10;
      
      // Add an elegant payment section header
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "bold");
      doc.text("INFORMACIÓN DE PAGO", summaryX, summaryY);
      
      // Subtle line beneath section header
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.1);
      doc.line(summaryX, summaryY + 3, pageWidth - margin, summaryY + 3);
      
      summaryY += 10;
      
      // Show amount paid
      summaryY = addSummaryRow("Abono:", 
        '$' + (factura.abono !== undefined && factura.abono !== null ? factura.abono.toFixed(2) : '0.00'), 
        summaryY);
      
      // Subtle line before pending balance
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.2);
      const balanceLineY = summaryY - 3;
      doc.line(summaryX + 30, balanceLineY, pageWidth - margin, balanceLineY);
      
      // Add a small space before balance
      summaryY += 2;
      
      // Show pending balance with slight emphasis
      summaryY = addSummaryRow("Saldo pendiente:", 
        '$' + (factura.saldoPendiente !== undefined && factura.saldoPendiente !== null ? factura.saldoPendiente.toFixed(2) : '0.00'), 
        summaryY, true);
    }
    
    // Update the current Y position after the summary
    currentY = summaryY + 10;
    
    // Check for page break before notes section
    if (factura.notas) {
      // Calculate approximate height needed for notes
      const splitNotes = doc.splitTextToSize(factura.notas, contentWidth - 12);
      const estimatedNotesHeight = splitNotes.length * 6 + 30; // Increased spacing for notes
      
      // Check and handle page break if needed
      currentY = checkPageBreak(currentY, estimatedNotesHeight);
      
      currentY += 20; // More space before notes
      
      doc.setFontSize(10);
      doc.setTextColor(colors.blueAccent);
      doc.setFont("helvetica", "bold");
      doc.text("NOTAS", margin, currentY);
      
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.5);
      doc.line(margin, currentY + 5, pageWidth - margin, currentY + 5);
      
      currentY += 15; // More space after header
      
      // Notes background with slightly rounded corners for better design
      doc.setFillColor(colors.grayBg);
      const actualNotesHeight = splitNotes.length * 6 + 10; // More padding
      doc.roundedRect(margin, currentY - 4, contentWidth, actualNotesHeight, 2, 2, 'F');
      
      // Notes text with better readability
      doc.setTextColor(colors.text);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(splitNotes, margin + 6, currentY);
      
      currentY += actualNotesHeight + 10;
    }
    
    // Agregar información sobre comisión de agencia si existe en la factura
    if (factura.reservation && 
        factura.reservation.type === 'b2b' && 
        'agenciaComision' in factura.reservation && 
        typeof factura.reservation.agenciaComision === 'number' && 
        factura.reservation.agenciaComision > 0) {
      // Calculate approximate height needed for commission info
      const estimatedCommissionHeight = 50; // Altura estimada para la sección de comisión
      
      // Check and handle page break if needed
      currentY = checkPageBreak(currentY, estimatedCommissionHeight);
      
      currentY += 15; // Más espacio antes de la sección de comisión
      
      doc.setFontSize(10);
      doc.setTextColor(colors.blueAccent);
      doc.setFont("helvetica", "bold");
      doc.text("INFORMACIÓN DE COMISIÓN", margin, currentY);
      
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.5);
      doc.line(margin, currentY + 5, pageWidth - margin, currentY + 5);
      
      currentY += 15; // Más espacio después del encabezado
      
      // Cuadro con fondo para la información de comisión
      doc.setFillColor(colors.blueLight);
      doc.roundedRect(margin, currentY - 4, contentWidth, 30, 2, 2, 'F');
      
      // Texto explicativo sobre la comisión
      doc.setTextColor(colors.text);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      
      const commissionText = [
        `Esta factura incluye una comisión para la agencia de $${factura.reservation.agenciaComision.toFixed(2)} (incluida en el precio total).`,
        "Este monto representa la comisión acordada con la agencia que ha vendido este servicio."
      ];
      
      doc.text(commissionText, margin + 6, currentY);
      
      currentY += 35; // Espacio después de la información de comisión
    }
    
    // Add footer to the last page
    addFooter(doc.getNumberOfPages());
    
    // Save the PDF file - make sure to sanitize the filename
    const sanitizedInvoiceNumber = factura.numero.replace(/[\/\\:*?"<>|]/g, '-');
    const pdfName = `factura_${sanitizedInvoiceNumber}.pdf`;
    doc.save(pdfName);
    
    onSuccess();
  } catch (error) {
    console.error("Error generando PDF:", error);
    onError(error);
  }
};

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}; 